import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    useMemo,
    useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import sportsApi from '../../../utils/sportsApi';

import { Card, Row, Col, LoadingDots } from '@app/ui';
import Tooltip from '@app/ui/sports-components/Tooltip';
import Checkbox from '@app/ui/sports-components/Checkbox';

import substringMatch from '../../../utils/substringMatch';
import '../../bulkSponsors.scss';

import getCount from '../../../utils/getCount';
import SingleSponsor from '../SingleSponsor';
import DraggableSponsor from '../DraggableSponsor';
import { darkGray } from '../../../constants/colors';

import { Context } from '../../contexts/OnboardingContext';
import { SponsorContext } from '../../contexts/SponsorContext';
import { getSponsorActive } from '../../../utils/getActiveOnboardingInstances';

const getNotDraggableReason = (sp, { sponsorsToAdd, sponsorsToRemove }, t) => {
    const hasLogo = sp?.logos && sp.logos.length > 0;
    if (!getSponsorActive(sp))
        return t('clientConfiguration.onboarding.deactivatedSponsorReason');
    if (!hasLogo) return t('clientConfiguration.onboarding.missingLogoReason');
    const inToAdd = sponsorsToAdd.findIndex(s => s.id == sp.id) > -1;
    const netAdds = getCount({ el: sp, arr: sponsorsToAdd });
    const netRemoves = getCount({ el: sp, arr: sponsorsToRemove });
    if (inToAdd && netAdds !== netRemoves)
        return t('clientConfiguration.onboarding.alreadyPendingReason');
    return null;
};

const ConfirmedSponsors = ({ client }) => {
    const { t } = useTranslation('settings');
    const {
        rhId,
        isLoading,
        structureLocked,
        hideInactiveAssets,
        isDraggableActive,
        isDragging,
        currentSponsorsLists,
        sponsorsToAdd,
        sponsorsToRemove,
        maxSponsors,
        setSelectedSponsors: setSelectedSponsorPlacements,
        selectedSponsors: selectedSponsorPlacements,
        isSelectingAllSponsors,
        setIsSelectingAllSponsors,
    } = useContext(Context);

    const {
        searchTerm,
        setSearchTerm,
        sponsors,
        setSponsors,
        showSponsorSelector,
        toAdd,
        setToAdd,
        toRemove,
        setToRemove,
        selected,
        setSelected,
        potentials,
        setPotentials,
        matchingSelected,
        toEdit,
        setToEdit,
        removeSponsor,
    } = useContext(SponsorContext);

    const [logoMessage, setLogoMessage] = useState(false);
    const [sponsorsLoading, setSponsorsLoading] = useState(false);
    const sponsorContainer = useRef(null);
    const scrollTopOnDragRef = useRef(0);
    const scrollableElRef = useRef(null);
    const scrollTopOnSelectRef = useRef(0);

    const hasMaxSponsors = !!maxSponsors;
    const panelLoading = isLoading || sponsorsLoading;

    const fetchSponsors = async (isCancelled = () => false) => {
        if (!rhId) return;
        setSponsorsLoading(true);
        try {
            const endPoint = `/clients/${client.id}/rightsholders/${rhId}/sponsors`;
            const sps = await sportsApi.url(endPoint).get().json();
            if (isCancelled()) return;
            // Sync selected in the same update cycle so the empty state does not
            // flash before Buttons' effect runs.
            setSponsors(sps);
            setSelected([...sps].sort((a, b) => a.name.localeCompare(b.name)));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        } finally {
            if (!isCancelled()) setSponsorsLoading(false);
        }
    };

    useEffect(() => {
        if (!rhId) return;
        let cancelled = false;
        fetchSponsors(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [rhId, client.id]);

    const addSponsors = sp => () => {
        if (toRemove.findIndex(el => sp.id === el.id) == -1) {
            setToAdd([
                ...toAdd,
                {
                    ...sp,
                    analysisActive: true,
                    active: true,
                    clientRemoved: false,
                },
            ]);
        } else {
            const newToRemove = toRemove.reduce((acc, el) => {
                if (el.id == sp.id) return acc;
                acc.push(el);
                return acc;
            }, []);
            setToRemove(newToRemove);
        }
        setSelected([sp, ...selected]);
        const newPotentials = potentials.reduce((acc, s) => {
            if (s.id !== sp.id) {
                acc.push(s);
            }
            return acc;
        }, []);
        setPotentials(newPotentials);
    };

    const deselectSponsor = sp => () => {
        if (toAdd.findIndex(el => sp.id === el.id) == -1) {
            setToRemove([...toRemove, sp]);
        } else {
            const newToAdd = toAdd.reduce((acc, el) => {
                if (el.id == sp.id) return acc;
                acc.push(el);
                return acc;
            }, []);
            setToAdd(newToAdd);
        }
        setPotentials([...potentials, sp]);
        const newSelected = selected.reduce((acc, s) => {
            if (s.id !== sp.id) {
                acc.push(s);
            }
            return acc;
        }, []);
        setSelected(newSelected);
    };

    const updateSearchTerm = e => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const _handleNavigation = e => {
        if (searchTerm.length === 0 || !showSponsorSelector) return;
        if (e.keyCode === 13) {
            const sp = matchingPotentials.find(
                sp => sp.name.toUpperCase() == searchTerm.toUpperCase(),
            );
            if (sp) {
                addSponsors(sp)();
            }
        }
    };

    useEffect(() => {
        const newLogoMessage = matchingSelected.reduce((acc, sp) => {
            const hasLogo = sp.logos && sp.logos.length > 0;
            acc = !!(acc || !hasLogo);
            return acc;
        }, false);
        setLogoMessage(newLogoMessage);
    }, [matchingSelected]);

    useEffect(() => {
        const getScrollableAncestor = el => {
            let node = el;
            while (node && node !== document.body) {
                const style = window.getComputedStyle(node);
                const overflowY = style.overflowY;
                const isScrollableY =
                    (overflowY === 'auto' || overflowY === 'scroll') &&
                    node.scrollHeight > node.clientHeight;
                if (isScrollableY) return node;
                node = node.parentElement;
            }
            return document.scrollingElement || document.documentElement;
        };
        const container = sponsorContainer.current;
        if (!container) return;
        if (isDragging) {
            const scrollable = getScrollableAncestor(container);
            scrollableElRef.current = scrollable;
            scrollTopOnDragRef.current = scrollable.scrollTop;
            return;
        }
        const id = requestAnimationFrame(() => {
            const el = scrollableElRef.current;
            if (el) el.scrollTop = scrollTopOnDragRef.current || 0;
        });
        return () => cancelAnimationFrame(id);
    }, [isDragging]);

    const prevIsDraggingRef = useRef(false);
    const prevSponsorsToAddLenRef = useRef(0);
    const prevCurrentSponsorsLenRef = useRef(0);

    useLayoutEffect(() => {
        const wasDragging = prevIsDraggingRef.current;
        const sponsorsToAddLenChanged =
            prevSponsorsToAddLenRef.current !== sponsorsToAdd.length;
        const currentSponsorsLenChanged =
            prevCurrentSponsorsLenRef.current !== currentSponsorsLists.length;

        if (
            wasDragging &&
            !isDragging &&
            (sponsorsToAddLenChanged || currentSponsorsLenChanged)
        ) {
            const el = scrollableElRef.current;
            if (el) {
                el.scrollTop = scrollTopOnDragRef.current || 0;
            }
        }

        prevIsDraggingRef.current = isDragging;
        prevSponsorsToAddLenRef.current = sponsorsToAdd.length;
        prevCurrentSponsorsLenRef.current = currentSponsorsLists.length;
    }, [isDragging, currentSponsorsLists, sponsorsToAdd]);

    useEffect(() => {
        document.addEventListener('keydown', _handleNavigation);
        return () => {
            document.removeEventListener('keydown', _handleNavigation);
        };
    }, [searchTerm]);

    const matchingPotentials = useMemo(() => {
        const exactPotentialMatch = potentials.find(
            p => p.name.toUpperCase() === searchTerm.toUpperCase(),
        );
        if (!exactPotentialMatch)
            return potentials
                .filter(
                    sp =>
                        substringMatch(searchTerm, sp.name) &&
                        sp.active &&
                        !sp.clientRemoved,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .slice(0, 30);
        return [
            exactPotentialMatch,
            ...potentials
                .filter(
                    sp =>
                        sp.name.toUpperCase() !== searchTerm.toUpperCase() &&
                        substringMatch(searchTerm, sp.name) &&
                        sp.active &&
                        !sp.clientRemoved,
                )
                .sort((a, b) => a.name.localeCompare(b.name)),
        ].slice(0, 30);
    }, [searchTerm, potentials]);

    const hasSelected = selected.length > 0;
    const showSponsorList = searchTerm.length > 0;
    const hasMatchingSelected = matchingSelected.length > 0;
    const hasMatchingPotentials = matchingPotentials.length > 0;
    const hasCurrentSponsorsLists = currentSponsorsLists.length > 0;
    const sponsorToEdit = sponsors.find(s => s.id === toEdit) || {};
    const orderedMatchingSelected = [
        ...matchingSelected.filter(sp => getSponsorActive(sp)),
        ...(hideInactiveAssets
            ? []
            : matchingSelected.filter(sp => !getSponsorActive(sp))),
    ];

    const sponsorsCount = useMemo(
        () => selected.filter(sp => getSponsorActive(sp)).length,
        [selected],
    );

    const partitionedSponsors = useMemo(() => {
        const ctx = { sponsorsToAdd, sponsorsToRemove };
        const draggable = [];
        const notDraggable = [];
        (currentSponsorsLists || []).forEach(sp => {
            if (!sp) return;
            const reason = getNotDraggableReason(sp, ctx, t);
            if (reason) notDraggable.push({ sp, reason });
            else draggable.push(sp);
        });
        draggable.sort((a, b) => a.name.localeCompare(b.name));
        notDraggable.sort((a, b) => a.sp.name.localeCompare(b.sp.name));
        return { draggable, notDraggable };
    }, [currentSponsorsLists, sponsorsToAdd, sponsorsToRemove]);

    const onToggleSelectAll = () => {
        let newSelectedSponsorPlacements = [];

        if (!isSelectingAllSponsors) {
            newSelectedSponsorPlacements = partitionedSponsors.draggable;
        }

        setSelectedSponsorPlacements(newSelectedSponsorPlacements);
        setIsSelectingAllSponsors(!isSelectingAllSponsors);
    };

    return (
        <>
            {panelLoading && (
                <div
                    className="gds-flex gds-flex--justify-center gds-flex--align-center -m-t-3"
                    style={{ minHeight: '300px', width: '100%' }}
                >
                    <LoadingDots size="lg" />
                </div>
            )}
            {!toEdit && !panelLoading && (
                <>
                    <div className="gds-flex gds-flex--align-center -m-t-2 ">
                        <div className="gds-flex__item gds-flex__item--grow-0 -m-r-3">
                            <label
                                className="gds-form-group__label -m-b-0"
                                htmlFor="input-2"
                            >
                                {hasMaxSponsors
                                    ? t(
                                          'clientConfiguration.onboarding.sponsorsCountWithMax',
                                          {
                                              count: sponsorsCount || 0,
                                              max: maxSponsors,
                                          },
                                      )
                                    : t(
                                          'clientConfiguration.onboarding.sponsorsCount',
                                          {
                                              count: sponsorsCount || 0,
                                          },
                                      )}
                            </label>
                        </div>
                        {hasMaxSponsors && (
                            <div className="gds-flex__item gds-flex__item--grow-1">
                                <div
                                    className="gds-progress-bar gds-progress-bar--secondary gds-progress-bar--xs -m-v-2"
                                    data-value={`${Math.floor(
                                        ((sponsorsCount || 0) / maxSponsors) *
                                            100,
                                    )}`}
                                />
                            </div>
                        )}
                    </div>

                    <div className="gds-form-group -m-t-2">
                        <Row gutter={16}>
                            <Col md={8}>
                                <Checkbox
                                    className="-cursor--default"
                                    inline
                                    label={t(
                                        'clientConfiguration.onboarding.selectAll',
                                    )}
                                    data-test="onboarding-select-all-sponsors"
                                    size="sm"
                                    checked={isSelectingAllSponsors}
                                    onChange={onToggleSelectAll}
                                    disabled={!isDraggableActive}
                                />
                            </Col>
                            <Col md={16}>
                                <input
                                    className="gds-form-group__text-input gds-form-group__text-input--sm"
                                    type="text"
                                    placeholder={
                                        showSponsorSelector
                                            ? t(
                                                  'clientConfiguration.onboarding.typeToSeeSponsors',
                                              )
                                            : t(
                                                  'clientConfiguration.onboarding.searchSponsor',
                                              )
                                    }
                                    data-gds-search-input=""
                                    id="searchTerm"
                                    value={searchTerm}
                                    onChange={updateSearchTerm}
                                />
                                <i className="gds-form-group__text-input-icon gds-form-group__text-input-icon--sm btl bt-search" />
                            </Col>
                        </Row>
                    </div>

                    {!hasSelected && !showSponsorSelector && (
                        <div
                            className="gds-flex gds-flex--justify-around gds-flex--content-center"
                            style={{ height: '300px' }}
                        >
                            <div className="gds-flex__item gds-flex__item--grow-0">
                                <p>
                                    {t(
                                        'clientConfiguration.onboarding.noSponsorsYet',
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {hasSelected && !showSponsorSelector && (
                        <>
                            <div
                                className="gds-flex-grid__container gds-sponsors-list gds-all-sponsors -p-a-2 -m-t-3"
                                ref={sponsorContainer}
                                id="droppableContainer_sponsors"
                                style={{ overflowAnchor: 'none' }}
                            >
                                {logoMessage && (
                                    <p className="gds-text--body-sm gds-text--italic gds-text--danger -m-b-1 --sponsor-logo-message">
                                        {t(
                                            'clientConfiguration.onboarding.missingLogoMessage',
                                        )}
                                    </p>
                                )}
                                <Row gutter={16}>
                                    {!hasMatchingSelected && (
                                        <Col md={24}>
                                            <div className="gds-flex gds-flex--justify-around">
                                                <div className="gds-flex__item gds-flex__item--grow-0 -text-center">
                                                    <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                    <p className="gds-text--body-sm gds-text--regular">
                                                        {t(
                                                            'clientConfiguration.onboarding.noMatchingSponsors',
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    {hasMatchingSelected &&
                                        isDraggableActive && (
                                            <>
                                                {!hasCurrentSponsorsLists && (
                                                    <Col md={24}>
                                                        <div className="gds-flex gds-flex--justify-around -m-t-3">
                                                            <div className="gds-flex__item gds-flex__item--grow-0 -text-center">
                                                                <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                                <p className="gds-text--body-sm gds-text--regular">
                                                                    {t(
                                                                        'clientConfiguration.onboarding.noMoreSponsors',
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                )}
                                                {hasCurrentSponsorsLists && (
                                                    <>
                                                        {partitionedSponsors.draggable.map(
                                                            sp => {
                                                                const hasLogo =
                                                                    sp.logos &&
                                                                    sp.logos
                                                                        .length >
                                                                        0;
                                                                const onclick =
                                                                    isSelected =>
                                                                    () => {
                                                                        const getScrollableAncestor =
                                                                            el => {
                                                                                let node =
                                                                                    el;
                                                                                while (
                                                                                    node &&
                                                                                    node !==
                                                                                        document.body
                                                                                ) {
                                                                                    const style =
                                                                                        window.getComputedStyle(
                                                                                            node,
                                                                                        );
                                                                                    const overflowY =
                                                                                        style.overflowY;
                                                                                    const isScrollableY =
                                                                                        (overflowY ===
                                                                                            'auto' ||
                                                                                            overflowY ===
                                                                                                'scroll') &&
                                                                                        node.scrollHeight >
                                                                                            node.clientHeight;
                                                                                    if (
                                                                                        isScrollableY
                                                                                    )
                                                                                        return node;
                                                                                    node =
                                                                                        node.parentElement;
                                                                                }
                                                                                return (
                                                                                    document.scrollingElement ||
                                                                                    document.documentElement
                                                                                );
                                                                            };
                                                                        const containerEl =
                                                                            sponsorContainer.current;
                                                                        const scrollable =
                                                                            containerEl
                                                                                ? getScrollableAncestor(
                                                                                      containerEl,
                                                                                  )
                                                                                : null;
                                                                        if (
                                                                            scrollable
                                                                        )
                                                                            scrollTopOnSelectRef.current =
                                                                                scrollable.scrollTop;
                                                                        if (
                                                                            !hasLogo
                                                                        )
                                                                            return;
                                                                        let newSelectedSponsorPlacements =
                                                                            [];
                                                                        if (
                                                                            !isSelected
                                                                        ) {
                                                                            newSelectedSponsorPlacements =
                                                                                [
                                                                                    ...selectedSponsorPlacements,
                                                                                    sp,
                                                                                ];
                                                                        } else {
                                                                            newSelectedSponsorPlacements =
                                                                                selectedSponsorPlacements.reduce(
                                                                                    (
                                                                                        acc,
                                                                                        s,
                                                                                    ) => {
                                                                                        if (
                                                                                            s.id ===
                                                                                            sp.id
                                                                                        )
                                                                                            return acc;
                                                                                        acc.push(
                                                                                            s,
                                                                                        );
                                                                                        return acc;
                                                                                    },
                                                                                    [],
                                                                                );
                                                                        }
                                                                        newSelectedSponsorPlacements =
                                                                            newSelectedSponsorPlacements.sort(
                                                                                (
                                                                                    a,
                                                                                    b,
                                                                                ) =>
                                                                                    a.name.localeCompare(
                                                                                        b.name,
                                                                                    ),
                                                                            );
                                                                        setSelectedSponsorPlacements(
                                                                            newSelectedSponsorPlacements,
                                                                        );
                                                                        if (
                                                                            isSelectingAllSponsors
                                                                        )
                                                                            setIsSelectingAllSponsors(
                                                                                false,
                                                                            );
                                                                        if (
                                                                            scrollable
                                                                        ) {
                                                                            requestAnimationFrame(
                                                                                () => {
                                                                                    scrollable.scrollTop =
                                                                                        scrollTopOnSelectRef.current ||
                                                                                        0;
                                                                                },
                                                                            );
                                                                        }
                                                                    };
                                                                return (
                                                                    <DraggableSponsor
                                                                        key={`${sp.id}`}
                                                                        id={`${sp.id}`}
                                                                        onclick={
                                                                            onclick
                                                                        }
                                                                        sponsor={
                                                                            sp
                                                                        }
                                                                        selectedSponsorPlacements={
                                                                            selectedSponsorPlacements
                                                                        }
                                                                        isDragDisabled={
                                                                            false
                                                                        }
                                                                        isDragging={
                                                                            isDragging
                                                                        }
                                                                    />
                                                                );
                                                            },
                                                        )}

                                                        {partitionedSponsors.notDraggable.map(
                                                            ({ sp }) => {
                                                                const hasLogo =
                                                                    sp.logos &&
                                                                    sp.logos
                                                                        .length >
                                                                        0;
                                                                const imgUrl =
                                                                    hasLogo
                                                                        ? sp
                                                                              .logos[0]
                                                                              .url
                                                                        : null;
                                                                const isActive =
                                                                    getSponsorActive(
                                                                        sp,
                                                                    );
                                                                return (
                                                                    <Col
                                                                        md={12}
                                                                        key={`nd_${sp.id}`}
                                                                    >
                                                                        <div className="gds-sponsor">
                                                                            <Card
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        isActive
                                                                                            ? '#fff'
                                                                                            : darkGray,
                                                                                }}
                                                                            >
                                                                                <div className="gds-flex gds-flex--align-center gds-text--body-sm -p-a-2">
                                                                                    <div className="gds-sponsors__image -m-l-1 -m-r-2 -color-bg-white">
                                                                                        {hasLogo && (
                                                                                            <img
                                                                                                src={
                                                                                                    imgUrl
                                                                                                }
                                                                                                height="100%"
                                                                                                width="100%"
                                                                                            />
                                                                                        )}
                                                                                        {!hasLogo && (
                                                                                            <div className="gds-flex gds-flex--justify-center gds-flex--content-center gds-flex-grid__item--full-height">
                                                                                                <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="gds-sponsor__name">
                                                                                        <div className="-p-r-1 ">
                                                                                            <p className="gds-text--body-sm gds-text--regular -ellipsis ">
                                                                                                {
                                                                                                    sp.name
                                                                                                }
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </Card>
                                                                        </div>
                                                                    </Col>
                                                                );
                                                            },
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    {hasMatchingSelected &&
                                        !isDraggableActive &&
                                        orderedMatchingSelected.map(
                                            (sp, index) => {
                                                const hasLogo =
                                                    sp.logos &&
                                                    sp.logos.length > 0;
                                                const imgUrl = hasLogo
                                                    ? sp.logos[0].url
                                                    : null;
                                                const openSingleSponsor =
                                                    sponsor => () => {
                                                        if (
                                                            !getSponsorActive(
                                                                sponsor,
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        setToEdit(sponsor.id);
                                                    };
                                                const isEven = index % 2 === 0;

                                                const isActive =
                                                    getSponsorActive(sp);

                                                return (
                                                    <Col
                                                        md={12}
                                                        key={`${sp.name}_${sp.id}_matching_selected`}
                                                    >
                                                        <div className="gds-sponsor">
                                                            <Card
                                                                style={{
                                                                    backgroundColor:
                                                                        isActive
                                                                            ? '#fff'
                                                                            : darkGray,
                                                                }}
                                                            >
                                                                <div className="gds-flex gds-flex--align-center gds-text--body-sm -p-a-2">
                                                                    <div className="gds-sponsors__image -m-l-1 -m-r-2 -color-bg-white">
                                                                        {hasLogo && (
                                                                            <img
                                                                                src={
                                                                                    imgUrl
                                                                                }
                                                                                height="100%"
                                                                                width="100%"
                                                                            />
                                                                        )}
                                                                        {!hasLogo && (
                                                                            <div className="gds-flex gds-flex--justify-center gds-flex--content-center gds-flex-grid__item--full-height">
                                                                                <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="gds-sponsor__name">
                                                                        <Tooltip
                                                                            text={
                                                                                isActive
                                                                                    ? sp.name
                                                                                    : t(
                                                                                          'clientConfiguration.onboarding.deactivatedSponsorTooltip',
                                                                                      )
                                                                            }
                                                                            position={
                                                                                isEven
                                                                                    ? 'bottom-right'
                                                                                    : 'bottom-left'
                                                                            }
                                                                            style={{
                                                                                width: '100%',
                                                                                verticalAlign:
                                                                                    'middle',
                                                                            }}
                                                                        >
                                                                            <div className="-p-r-1 ">
                                                                                <p className="gds-text--body-sm gds-text--regular -ellipsis ">
                                                                                    {
                                                                                        sp.name
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                        </Tooltip>
                                                                    </div>
                                                                    {isActive && (
                                                                        <div
                                                                            className="gds-flex gds-flex gds-flex--direction-col"
                                                                            style={{
                                                                                minWidth:
                                                                                    '16px',
                                                                                minHeight:
                                                                                    '48px',
                                                                            }}
                                                                        >
                                                                            <i
                                                                                className="fa fa-fw fa-pencil gds-text--body-sm -cursor--pointer -color-tx-dk-5 -m-t-1 -m-b-2"
                                                                                onClick={openSingleSponsor(
                                                                                    sp,
                                                                                )}
                                                                            />
                                                                            {!structureLocked && (
                                                                                <i
                                                                                    className="fa fa-fw fa-trash-alt gds-text--body-sm -cursor--pointer -color-tx-dk-5 -m-b-1 -m-t-2"
                                                                                    onClick={removeSponsor(
                                                                                        sp,
                                                                                    )}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Card>
                                                        </div>
                                                    </Col>
                                                );
                                            },
                                        )}
                                </Row>
                            </div>
                        </>
                    )}

                    {showSponsorSelector && (
                        <>
                            <p className="gds-text--body-sm gds-text--italic gds-text--danger -m-t-2">
                                {t(
                                    'clientConfiguration.onboarding.typeToSeeSponsors',
                                )}
                            </p>

                            <div className="gds-flex-grid__container">
                                <div className="gds-flex-grid__row">
                                    <div className="gds-flex-grid__item gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-2 gds-flex-grid__item--mobile-1 ">
                                        <label className="gds-form-group__label -m-b-0">
                                            {t(
                                                'clientConfiguration.onboarding.matchingSponsors',
                                            )}
                                        </label>
                                    </div>
                                    <div className="gds-flex-grid__item gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-2 gds-flex-grid__item--mobile-1 ">
                                        <label className="gds-form-group__label -m-b-0">
                                            {t(
                                                'clientConfiguration.onboarding.selectedSponsors',
                                            )}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="gds-flex-grid__container">
                                <Row gutter={16}>
                                    <Col md={12}>
                                        <div className="gds-all-sponsors -p-a-2">
                                            {showSponsorList && (
                                                <>
                                                    {!hasMatchingPotentials && (
                                                        <div className="gds-flex gds-flex--justify-around">
                                                            <div className="gds-flex__item gds-flex__item--grow-0 -text-center">
                                                                <i className="fa fa-fw fa-exclamation-triangle gds-text--danger" />
                                                                <p className="gds-text--body-sm gds-text--regular">
                                                                    {t(
                                                                        'clientConfiguration.onboarding.noMatchingSponsors',
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {hasMatchingPotentials &&
                                                        matchingPotentials.map(
                                                            sp => (
                                                                <Card
                                                                    data-test="social-post"
                                                                    key={`${sp.name}_${sp.id}_potentials`}
                                                                    onClick={addSponsors(
                                                                        sp,
                                                                    )}
                                                                >
                                                                    <div className="gds-flex gds-flex--align-center gds-text--body-sm -p-l-3 -p-r-2 -p-v-2">
                                                                        <div className="gds-sponsor__name">
                                                                            <p className="gds-text--body-sm gds-text--regular -ellipsis -p-r-1">
                                                                                {
                                                                                    sp.name
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                        <i className="fa fa-fw fa-plus-circle gds-text--success gds-text--body-md -cursor--pointer" />
                                                                    </div>
                                                                </Card>
                                                            ),
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div className="gds-all-sponsors -p-a-2">
                                            {selected.map(sp => (
                                                <Card
                                                    data-test="social-post"
                                                    key={`${sp.name}_${sp.id}_toremove_selected`}
                                                    onClick={deselectSponsor(
                                                        sp,
                                                    )}
                                                >
                                                    <div className="gds-flex gds-flex--align-center gds-text--body-sm -p-l-3 -p-r-2 -p-v-2">
                                                        <div className="gds-sponsor__name">
                                                            <p className="gds-text--body-sm gds-text--regular -ellipsis -p-r-1">
                                                                {sp.name}
                                                            </p>
                                                        </div>
                                                        <i className="fa fa-fw fa-times-circle gds-text--danger gds-text--body-md -cursor--pointer" />
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}
                </>
            )}
            {toEdit && !panelLoading && (
                <SingleSponsor
                    sponsor={sponsorToEdit}
                    fetchSponsors={fetchSponsors}
                />
            )}
        </>
    );
};

ConfirmedSponsors.propTypes = {
    client: PropTypes.object,
};

export default connect(['client'])(ConfirmedSponsors);
